import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import {api, LegalDocument} from '../../services/api'
import {toast} from '../../utils/toast'

// Toolbar for the rich-text editor — headings, emphasis, lists, links, and a
// "clear formatting" button. Output is the HTML stored in `content` and rendered
// verbatim on the landing page + mobile app.
const QUILL_MODULES = {
  toolbar: [
    [{header: [2, 3, false]}],
    ['bold', 'italic', 'underline'],
    [{list: 'ordered'}, {list: 'bullet'}],
    ['link'],
    ['clean'],
  ],
}

const Toggle: FC<{checked: boolean; onChange: () => void}> = ({checked, onChange}) => (
  <div onClick={onChange} style={{
    width: 40, height: 22, borderRadius: 11, cursor: 'pointer', flexShrink: 0,
    background: checked ? '#22c55e' : '#e2e8f0', position: 'relative', transition: 'background 0.2s',
  }}>
    <div style={{
      position: 'absolute', top: 3, left: checked ? 21 : 3,
      width: 16, height: 16, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
    }} />
  </div>
)

const LegalDocumentsPage: FC = () => {
  const [docs, setDocs] = useState<LegalDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')

  const [editTarget, setEditTarget] = useState<LegalDocument | null>(null)
  const [formTitle, setFormTitle] = useState('')
  const [formCategory, setFormCategory] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formPublished, setFormPublished] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const list = await api.getLegalDocuments()
      setDocs(list)
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load documents')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return docs
    return docs.filter(d =>
      d.title.toLowerCase().includes(term) ||
      d.category.toLowerCase().includes(term) ||
      d.slug.toLowerCase().includes(term),
    )
  }, [docs, search])

  // Dropdown options — the standard set plus any categories already in use.
  const categoryOptions = useMemo(() => {
    const set = new Set<string>(['Terms', 'Policies', 'Guidelines', 'Agreements'])
    docs.forEach(d => { if (d.category) set.add(d.category) })
    return Array.from(set)
  }, [docs])

  const openEdit = (d: LegalDocument) => {
    setEditTarget(d)
    setFormTitle(d.title)
    setFormCategory(d.category)
    setFormContent(d.content)
    setFormPublished(d.isPublished)
    setFormError('')
  }

  const handleSave = async () => {
    if (!editTarget) return
    if (!formTitle.trim()) { setFormError('Title is required.'); return }
    setSaving(true)
    try {
      await api.updateLegalDocument(editTarget.id, {
        title: formTitle.trim(),
        category: formCategory.trim() || 'General',
        content: formContent,
        isPublished: formPublished,
      })
      setEditTarget(null)
      load()
      toast('Document saved')
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save.')
      toast(e?.message ?? 'Save failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  const togglePublished = async (d: LegalDocument) => {
    try {
      await api.updateLegalDocument(d.id, {isPublished: !d.isPublished})
      load()
      toast(d.isPublished ? 'Document unpublished' : 'Document published')
    } catch (e: any) {
      toast(e?.message ?? 'Update failed', 'error')
    }
  }

  const inputStyle = {
    width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc',
    outline: 'none', boxSizing: 'border-box' as const,
  }
  const labelStyle = {fontSize: 13, fontWeight: 600 as const, color: '#374151', display: 'block' as const, marginBottom: 6}

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Legal Documents</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Edit the policy &amp; terms pages shown on the landing page and inside the mobile app</p>
      </div>

      {/* Toolbar */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '16px 20px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', gap: 12, alignItems: 'center',
      }}>
        <div style={{position: 'relative', flex: 1}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input type='text' placeholder='Search documents...' value={search}
            onChange={e => setSearch(e.target.value)}
            style={{...inputStyle, paddingLeft: 38, background: '#fff'}} />
        </div>
        <button onClick={load} disabled={loading}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/>
            <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={load} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['DOCUMENT', 'CATEGORY', 'STATUS', 'UPDATED', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, i) => (
                  <tr key={item.id}
                    style={{borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                    onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{padding: '14px 20px', minWidth: 220}}>
                      <span style={{fontWeight: 700, color: '#0f172a', fontSize: 14}}>{item.title}</span>
                      <span style={{display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 3}}>/{item.slug}</span>
                    </td>
                    <td style={{padding: '14px 20px'}}>
                      <span style={{fontSize: 12, fontWeight: 600, color: '#475569', background: '#f1f5f9', padding: '3px 10px', borderRadius: 20}}>{item.category}</span>
                    </td>
                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <Toggle checked={item.isPublished} onChange={() => togglePublished(item)} />
                        <span style={{fontSize: 13, fontWeight: 500, color: item.isPublished ? '#16a34a' : '#94a3b8'}}>
                          {item.isPublished ? 'Published' : 'Hidden'}
                        </span>
                      </div>
                    </td>
                    <td style={{padding: '14px 20px', color: '#64748b', fontSize: 13, whiteSpace: 'nowrap'}}>
                      {new Date(item.updatedAt).toLocaleDateString()}
                    </td>
                    <td style={{padding: '14px 20px'}}>
                      <button onClick={() => openEdit(item)}
                        style={{padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer'}}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No documents found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', fontSize: 13, color: '#94a3b8'}}>
            {filtered.length} of {docs.length} documents · {docs.filter(d => d.isPublished).length} published
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editTarget && (
        <div onClick={() => !saving && setEditTarget(null)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 760, maxWidth: '100%', maxHeight: '92vh', boxShadow: '0 24px 64px rgba(0,0,0,0.15)', overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
            <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0}}>Edit Document</h3>
              <button onClick={() => !saving && setEditTarget(null)}
                style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4}}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
              </button>
            </div>

            <div style={{padding: '24px', display: 'flex', flexDirection: 'column', gap: 18, overflowY: 'auto'}}>
              {formError && (
                <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13}}>{formError}</div>
              )}
              <div style={{display: 'flex', gap: 14}}>
                <div style={{flex: 2}}>
                  <label style={labelStyle}>Title <span style={{color: '#ef4444'}}>*</span></label>
                  <input value={formTitle} onChange={e => setFormTitle(e.target.value)} style={inputStyle} />
                </div>
                <div style={{flex: 1}}>
                  <label style={labelStyle}>Category</label>
                  <select value={formCategory} onChange={e => setFormCategory(e.target.value)} style={inputStyle}>
                    {categoryOptions.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{fontSize: 12, color: '#94a3b8'}}>Slug (fixed): <code>/{editTarget.slug}</code></div>

              <div>
                <label style={labelStyle}>Content</label>
                <div style={{border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden'}}>
                  <ReactQuill theme='snow' value={formContent} onChange={setFormContent} modules={QUILL_MODULES} style={{minHeight: 320}} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Visibility</label>
                <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                  <Toggle checked={formPublished} onChange={() => setFormPublished(p => !p)} />
                  <span style={{fontSize: 14, fontWeight: 500, color: formPublished ? '#16a34a' : '#94a3b8'}}>
                    {formPublished ? 'Published' : 'Hidden'}
                  </span>
                </div>
                <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>Hidden documents are not returned to the landing page or mobile app.</div>
              </div>
            </div>

            <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10}}>
              <button onClick={() => setEditTarget(null)} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1}}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .ql-container { min-height: 280px; font-size: 14px; }`}</style>
    </div>
  )
}

const LegalDocumentsPageWrapper: FC = () => <LegalDocumentsPage />

export {LegalDocumentsPageWrapper}
